import { ComponentType } from 'react';
import { AllZonesIcon } from 'src/svgs/MapZoneIcons/AllZonesIcon';
import { BalconyIcon } from 'src/svgs/MapZoneIcons/BalconyIcon';
import { ClubLodgeBoxIcon } from 'src/svgs/MapZoneIcons/ClubLodgeBoxIcon';
import { FloorIcon } from 'src/svgs/MapZoneIcons/FloorIcon';
import { LowerIcon } from 'src/svgs/MapZoneIcons/LowerIcon';
import { MezzanineIcon } from 'src/svgs/MapZoneIcons/MezzanineIcon';
import { MiddleIcon } from 'src/svgs/MapZoneIcons/MiddleIcon';
import { OrchestraIcon } from 'src/svgs/MapZoneIcons/OrchestraIcon';
import { OthersIcon } from 'src/svgs/MapZoneIcons/OthersIcon';
import { ParkingIcon } from 'src/svgs/MapZoneIcons/ParkingIcon';
import { UpperIcon } from 'src/svgs/MapZoneIcons/UpperIcon';
import { PosIconProps } from 'src/svgs/SvgWrapper';

// Factory to create text-based zone icon components
const createTextZoneIcon = (text: string): ComponentType<PosIconProps> => {
  const TextIcon = ({ size, title }: PosIconProps) => (
    <span
      style={{
        width: size || '40px',
        height: size || '40px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: text.length > 2 ? '12px' : text.length > 1 ? '14px' : '18px',
        color: 'currentColor',
        border: '1.5px solid currentColor',
        borderRadius: '6px',
        boxSizing: 'border-box' as const,
        lineHeight: 1,
        flexShrink: 0,
      }}
      title={title}
    >
      {text}
    </span>
  );
  TextIcon.displayName = `TextZoneIcon_${text}`;
  return TextIcon;
};

// Pre-created text zone icons (module-level to avoid React remounts)
const GAIcon = createTextZoneIcon('GA');
const VIPIcon = createTextZoneIcon('VIP');
const Cat1Icon = createTextZoneIcon('1');
const Cat2Icon = createTextZoneIcon('2');
const Cat3Icon = createTextZoneIcon('3');
const Cat4Icon = createTextZoneIcon('4');
const Cat5Icon = createTextZoneIcon('5');

/**
 * Zone-to-icon mapping rules derived from the ticket class super-group CSV.
 * Rules are evaluated top-to-bottom; first match wins.
 * All patterns are tested case-insensitively against the ticket class name.
 */
const zoneIconRules: Array<{
  pattern: RegExp;
  icon: ComponentType<PosIconProps>;
}> = [
  // Category zones (text icons "1"–"5") — check first, most specific
  { pattern: /^(category|kategorie)\s*1/i, icon: Cat1Icon },
  { pattern: /^(category|kategorie)\s*2/i, icon: Cat2Icon },
  { pattern: /^(category|kategorie)\s*3/i, icon: Cat3Icon },
  { pattern: /^(category|kategorie)\s*4/i, icon: Cat4Icon },
  {
    pattern: /^(category|kategorie)\s*[5-9]|^tier\s|^tiered\s*seating$/i,
    icon: Cat5Icon,
  },

  // Premium / VIP / Hospitality → text "VIP"
  {
    pattern:
      /^(vip|premium|platinum|gold(?!\s*circle)|preferred|premier|flagship|hospitality|courtside|front\s*(row|of\s*stage|stage)|suite|skybox|dugout|executive\s*seats$|the\s*key$|lounge$|bronze\s*star$)/i,
    icon: VIPIcon,
  },

  // Floor / Field / Pit → FloorIcon SVG  (checked before GA so "ga floor" maps here)
  {
    pattern:
      /^(floor|main\s*floor|ga\s*floor|field|pitch|turf$|pelouse$|outfield$|home\s*run\s*porch|pit|innenraum|campo$|pista)/i,
    icon: FloorIcon,
  },

  // GA / General Admission → text "GA"  (remaining GA patterns after Floor is checked)
  {
    pattern:
      /^(general(\s*admission|\s*[ab]$|$)|ga(\s|$)|standing|admission\s*ticket|stehplatz|supporters)/i,
    icon: GAIcon,
  },

  // Parking
  {
    pattern: /(parking|lot$|lots$|garage|within\s*0\.\d+\s*mi)/i,
    icon: ParkingIcon,
  },

  // Theater zones
  { pattern: /orchestra/i, icon: OrchestraIcon },
  { pattern: /mezzanine/i, icon: MezzanineIcon },
  { pattern: /balcony/i, icon: BalconyIcon },

  // Stadium zones
  {
    pattern:
      /^(lower|100[\s-]*level|level\s*1(00)?$|street\s*100|ground\s*level|first\s*(tier|base)|third\s*base|baseline|home\s*plate|infield|sideline|main(\s|$))/i,
    icon: LowerIcon,
  },
  {
    pattern:
      /^(upper|[3-5]00[\s-]*level|level\s*[3-5](00)?$|top\s*deck|gallery|family\s*circle|promenade|[a-c]-?deck|aa-?deck|view|higher\s*upper)/i,
    icon: UpperIcon,
  },
  {
    pattern:
      /(club|loge|prime\s*loge$|^box|boxes$|diamond|delta\s*sky|rogers\s*banner|hall\s*of\s*fame|chase\s*bridges|american\s*airlines|annexus|motorcity|t-mobile\s*zone|blueprint)/i,
    icon: ClubLodgeBoxIcon,
  },
  {
    pattern:
      /^(middle|200[\s-]*level|level\s*2(00)?$|street\s*200|second\s*(tier|ring)|mid\s*level|debenture)/i,
    icon: MiddleIcon,
  },
];

export const getZoneIcon = (
  zoneName: string | undefined
): ComponentType<PosIconProps> => {
  if (!zoneName) return AllZonesIcon;

  for (const rule of zoneIconRules) {
    if (rule.pattern.test(zoneName)) {
      return rule.icon;
    }
  }

  return OthersIcon;
};
